import axios from "axios";
import { useFormik } from "formik";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import ReactSelect from "../../../Components/PortalSelect";
import { AssignSalesExecutiveSchema } from "../../../ValidationSchema";
import { Autocomplete, TextField } from "@mui/material";
import Modal from "../../../Components/Modal";

const AssignSalesExecutiveModal = ({
  show,
  userId,
  usertype,
  orderType,
  onClose,
}) => {
  const Navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const [selectType, setSelectType] = useState("single"); // "single" | "multiple"
  const [assignRole, setAssignRole] = useState("se"); // "gd" | "se" (only for Admin)
  const isSubAdmin = user?.user_type === "subadmin";
  const isAdmin = user?.user_type === "admin";

  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [totalpage, setTotalPage] = useState(0);
  const [page, setPage] = useState(1);
  const [buyerOptions, setBuyerOptions] = useState([]);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [allExecutives, setAllExecutives] = useState([]); // ✅ raw data

  const handlePageClick = (e) => {
    setPage(e.selected + 1);
  };

  // 🔥 Role-based label + placeholder
  const labelText =
    isSubAdmin || (isAdmin && assignRole === "gd")
      ? "Graphic Designer"
      : "Sales Executive";

  const placeholderText =
    isSubAdmin || (isAdmin && assignRole === "gd")
      ? "Select Graphic Designer"
      : "Select Sales Executive";

  const colourStyles = {
    control: (styles) => ({
      ...styles,
      border: "1px solid #bfc9d4",
      boxShadow: "#000 !important",
      borderRadius: "6px",
      padding: "4px",
      "&:hover": {
        border: "1px solid #bfc9d4",
      },
    }),
    multiValue: (styles) => ({
      ...styles,
      backgroundColor: "#606060",
    }),
    multiValueLabel: (styles) => ({
      ...styles,
      color: "#fff",
    }),
    multiValueRemove: (styles, { data }) => ({
      ...styles,
      color: "#000",
      "& > svg": {
        backgroundColor: "#fff",
        borderRadius: "10px",
        marginLeft: "2px",
        marginRight: "2px",
      },
      ":hover": {
        backgroundColor: data.color,
        color: data.color,
      },
    }),
    option: (styles) => ({
      ...styles,
      backgroundColor: "white",
      color: "#000",
      ":hover": {
        backgroundColor: "#f5f5f5",
      },
    }),
  };

  const SalesExecutiveData = () => {
    axios
      .get(`${process.env.REACT_APP_URL}/admin/users`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("Token")}`,
        },
      })
      .then((res) => {
        const subadmins = res?.data?.data?.users.filter((s) => {
          return s?.sub_admin_id === 1 || s?.sub_admin_id === 2;
        });
        const execs = subadmins.map((item) => ({
          label: item?.name || "Untitled",
          value: item?.user_id,
          sub_admin_id: item?.sub_admin_id, // Sales Executive / Graphic Designer
        }));
        setAllExecutives(execs);
      })
      .catch((err) => {
        if (err.response?.data?.message === "Unauthorized!") {
          Navigate("/");
        }
      });
  };

  const BuyerLeadData = () => {
    const searchQuery = search ? `&search=${search}` : "";

    axios
      .get(
        `${process.env.REACT_APP_URL}/admin/offline_buyer/order/${userId}?page=${page}${searchQuery}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("Token")}`,
          },
        }
      )
      .then((res) => {
        setTotalPage(res?.data?.data?.pages);
        setData(res?.data?.data?.orders);

        let leads = res?.data?.data?.orders || [];

        if (user?.user_type === "admin") {
          leads = leads.filter(
            (item) =>
              item?.sales_executive_id === null ||
              item?.graphic_designer_id === null
          );
        } else if (user?.user_type === "subadmin") {
          leads = leads.filter(
            (item) =>
              item?.graphic_designer_id === null &&
              item?.sales_executive_id === user?.user_id
          );
        }

        const buyers = leads.map((item) => ({
          label: `Order#${item?.order_id}_${item?.title}`,
          value: item?.order_id,
          product_id: item?.product_id,
        }));

        setBuyerOptions(buyers);
      })
      .catch((err) => {
        if (err?.response?.data?.message === "Unauthorized!") {
          Navigate("/");
        }
      });
  };

  const SupplierData = () => {
    const searchQuery = search ? `&search=${search}` : "";

    axios
      .get(
        `${process.env.REACT_APP_URL}/admin/orders?page=${page}${searchQuery}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("Token")}`,
          },
        }
      )
      .then((res) => {
        setTotalPage(res?.data?.data?.pages);
        setData(res?.data?.data?.orders);

        let leads = res?.data?.data?.orders || [];
        leads = leads.filter((item) => {
          return item?.user_id === parseInt(userId);
        });
        // Filter based on user type and user ID
        if (user?.user_type === "admin") {
          leads = leads.filter(
            (item) =>
              item?.sales_executive_id === null ||
              item?.graphic_designer_id === null
          );
        } else if (user?.user_type === "subadmin") {
          leads = leads.filter(
            (item) =>
              item?.graphic_designer_id === null &&
              item?.sales_executive_id === user?.user_id
          );
        }

        const suppliers = leads.map((item) => ({
          label: `Order#${item?.order_id}_${item?.title}`,
          value: item?.order_id || item?.id,
          product_id: item?.product_id,
        }));

        setSupplierOptions(suppliers);
      })
      .catch((err) => {
        if (err?.response?.data?.message === "Unauthorized!") {
          Navigate("/");
        }
      });
  };

  const [initialValues] = useState({
    sales_executive_id: "",
    order_id: [],
    supplier_id: "",
  });

  const AddSubCategory = useFormik({
    initialValues,
    validationSchema: AssignSalesExecutiveSchema,
    onSubmit: (values, action) => {
      // if (usertype === "supplier") {
      //   data = {
      //     userId: values?.supplier_id,
      //     sales_executive_id: values.sales_executive_id,
      //     role: assignRole,
      //   };
      // } else {
      const data = {
        ids: values.order_id.map((o) => ({
          product_id: o.product_id,
          order_id: o.order_id,
        })),
      };

      // }

      axios
        .post(
          `${process.env.REACT_APP_URL}/admin/quotation/send/subAdmin/${values.sales_executive_id}`,
          data,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("Token")}`,
            },
          }
        )
        .then((res) => {
          if (res.status === 201) {
            toast.success(res?.data?.message);

            if (orderType === "Offline Supplier") {
              Navigate(`/admin/supplier-offline-product-list/${userId}`, {
                state: res.data.msg,
              });
            } else {
              Navigate(`/admin/buyer/product-list/${userId}`, {
                state: res.data.msg,
              });
            }

            // ✅ Wait 500ms then reload to ensure new data is fetched
            setTimeout(() => {
              window.location.reload();
            }, 500);

            onClose();
          }
        })

        .catch((err) => {
          toast.error(err.response?.data?.message || "Failed to assign");
        });
    },
  });

  React.useEffect(() => {
    SalesExecutiveData();
    BuyerLeadData();
    SupplierData();
  }, [orderType]);

  const orderOptions = usertype === "supplier" ? supplierOptions : buyerOptions;

  // When there's only one order to pick from, there's nothing to actually
  // choose — auto-select it instead of making the admin open the dropdown
  // just to pick the only thing in it.
  React.useEffect(() => {
    if (
      orderOptions.length === 1 &&
      AddSubCategory.values.order_id.length === 0
    ) {
      const only = orderOptions[0];
      AddSubCategory.setFieldValue("order_id", [
        { order_id: only.value, product_id: only.product_id },
      ]);
    }
  }, [orderOptions]);

  const filteredExecutives = allExecutives.filter((item) => {
    if (isSubAdmin) {
      return item.sub_admin_id === 1; // GD for sub-admin
    } else if (isAdmin) {
      if (assignRole === "se") {
        return item.sub_admin_id === 2; // SE for admin
      } else if (assignRole === "gd") {
        return item.sub_admin_id === 1; // GD for admin
      }
    }
    return false;
  });

  const heading = isSubAdmin
    ? "Assign Graphic Designer"
    : isAdmin
    ? "Assign Role"
    : "Assign Sales Executive";

  return (
    <Modal
      open={!!show}
      onClose={onClose}
      title={heading}
      subtitle="Pick the order, then the team member who should own it."
      size="md"
      footer={
        <>
          <button type="button" className="al-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="al-btn al-btn--primary"
            onClick={AddSubCategory.handleSubmit}
          >
            Assign
          </button>
        </>
      }
    >
      {/* GD/SE choice, admin only — segmented rather than bare radios so the
          two options read as a single decision. */}
      {isAdmin && (
        <div className="al-field">
          <label className="al-label">Assign to</label>
          <div className="al-seg">
            {[
              { key: "se", label: "Sales Executive" },
              { key: "gd", label: "Graphic Designer" },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                className={`al-seg__i ${
                  assignRole === opt.key ? "is-active" : ""
                }`}
                onClick={() => {
                  setAssignRole(opt.key);
                  // filteredExecutives switches to the other role's list on
                  // toggle, but the previously picked person's id stayed in
                  // sales_executive_id — invisible in the dropdown (it no
                  // longer matches any option so it read as empty) but still
                  // there to be submitted if the admin didn't notice and
                  // re-pick. The same person can't be both SE and GD, so
                  // switching roles has to clear the selection outright.
                  AddSubCategory.setFieldValue("sales_executive_id", "");
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

          {/* 🔘 Buyer/Supplier Selection Type */}
          {/* <div className="mb-3">
            <label
              style={{ fontSize: "14px", color: "black", marginRight: "10px" }}
            >
              <input
                type="radio"
                name="selectType"
                value="single"
                checked={selectType === "single"}
                onChange={() => setSelectType("single")}
                style={{ marginRight: "5px" }}
              />
              Single
            </label>
            <label style={{ fontSize: "14px", color: "black" }}>
              <input
                type="radio"
                name="selectType"
                value="multiple"
                checked={selectType === "multiple"}
                onChange={() => setSelectType("multiple")}
                style={{ marginRight: "5px" }}
              />
              Multiple
            </label>
          </div> */}

          {/* Buyer / Supplier Dropdown */}
          {/* {selectType === "single" && ( */}
          <div className="al-field">
            <label className="al-label">Order</label>
            <ReactSelect
              options={orderOptions}
              isClearable={false}
              styles={colourStyles}
              components={{
                DropdownIndicator: () => null,
                IndicatorSeparator: () => null,
              }}
              placeholder={`Select Order`}
              name={"order_id"}
              onChange={(selectedOption) => {
                AddSubCategory.setFieldValue(
                  "order_id",
                  selectedOption
                    ? [
                        {
                          order_id: selectedOption.value,
                          product_id: selectedOption.product_id,
                        },
                      ]
                    : []
                );
              }}
              // Was uncontrolled — picking (or auto-picking, above) an order
              // never showed in the field itself, it only ever reached
              // formik state invisibly.
              value={
                orderOptions.find(
                  (opt) =>
                    opt.value === AddSubCategory.values.order_id?.[0]?.order_id
                ) || null
              }
            />
          </div>
          {/* )} */}

          {/* {selectType === "multiple" && (
            <div className="col-md-12 mb-3">
              <label style={{ fontSize: "14px", color: "black" }}>
                Multiple {usertype === "supplier" ? "Suppliers" : "Buyers"}
              </label>
              <Autocomplete
                multiple
                filterSelectedOptions // ✅ This hides already selected options
                options={
                  usertype === "supplier" ? supplierOptions : buyerOptions
                }
                getOptionLabel={(option) => option?.label}
                onChange={(event, value) => {
                  const ids = value.map((item) => ({
                    order_id: item.value,
                    product_id: item.product_id,
                  }));
                  AddSubCategory.setFieldValue("order_id", ids);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={`Select ${
                      usertype === "supplier" ? "Suppliers" : "Buyers"
                    }`}
                    variant="outlined"
                    size="small"
                    style={{ marginTop: "5px" }}
                  />
                )}
              />
            </div>
          )} */}

          {/* 🔥 GD/SE Dropdown */}
          <div className="al-field">
            <label className="al-label">{labelText}</label>
            <ReactSelect
              options={filteredExecutives} // ✅ filtered list
              isClearable={false}
              styles={colourStyles}
              components={{
                DropdownIndicator: () => null,
                IndicatorSeparator: () => null,
              }}
              placeholder={placeholderText}
              name="sales_executive_id"
              onChange={(selectedOption) => {
                AddSubCategory.setFieldValue(
                  "sales_executive_id",
                  selectedOption ? selectedOption.value : ""
                );
                AddSubCategory.setFieldValue("role", assignRole);
              }}
              value={filteredExecutives.find(
                (item) =>
                  item?.value === AddSubCategory?.values?.sales_executive_id
              )}
            />
          </div>
    </Modal>
  );
};

export default AssignSalesExecutiveModal;
